import { ArsTextJustification } from "../../table/interface";
import { isCaseWhenCondition, isObjectRecord } from "../../typeGuards";
import type { ArsListingColumn, ArsListingTextAlignment } from "../interface";
import { isArsListingVariable } from "./isArsListingVariable";

const arsTextJustifications: Set<string> = new Set(Object.values(ArsTextJustification));

/**
 * Validates if a value is a valid ArsTextAlignment.
 *
 * @param value - Unknown value to validate
 * @returns True when value is one of the ArsTextJustification enum values
 */
function isArsTextJustification(value: unknown): value is ArsListingTextAlignment {
  return typeof value === "string" && arsTextJustifications.has(value);
}

/**
 * Type guard for ArsListing column definitions.
 *
 * @param obj - Unknown value to validate
 * @param isT - Optional type guard for the generic type T used in displayPattern and headerText
 * @returns True when value can be treated as ArsListingColumn
 */
export const isArsListingColumn = <T = string>(
  obj: unknown,
  isT: (value: unknown) => value is T = (value: unknown): value is T => typeof value === "string",
): obj is ArsListingColumn => {
  // Check object
  if (!isObjectRecord(obj)) return false;

  // Check required properties
  if (typeof obj.id !== "string") return false;
  if (typeof obj.columnWidth !== "number") return false;
  if (!["number", "undefined"].includes(typeof obj.decimalAlignPercent)) return false;
  if (obj.displayPattern === undefined || obj.displayPattern === null) return false;
  if (Array.isArray(obj.displayPattern) && obj.displayPattern.some((item) => !isT(item)))
    return false;
  if (!Array.isArray(obj.displayPattern) && !isT(obj.displayPattern)) return false;
  if (!isT(obj.headerText)) return false;
  if (typeof obj.resultPattern !== "string") return false;

  // Check optional properties if present
  if (obj.bodyTextAlignment !== undefined && !isArsTextJustification(obj.bodyTextAlignment)) {
    return false;
  }
  if (obj.headerHeight !== undefined && typeof obj.headerHeight !== "number") {
    return false;
  }
  if (obj.headerTextAlignment !== undefined && !isArsTextJustification(obj.headerTextAlignment)) {
    return false;
  }
  if (
    obj.variables !== undefined &&
    (!Array.isArray(obj.variables) || obj.variables.some((v) => !isArsListingVariable(v)))
  ) {
    return false;
  }
  if (obj.caseWhenCondition !== undefined && !isCaseWhenCondition(obj.caseWhenCondition, isT)) {
    return false;
  }

  // Everything is good
  return true;
};
